/**
 * HrEmployeeFormModal 컴포넌트 (V48 + V26.2)
 *
 * **역할:**
 * - 단일 직원 추가 폼을 모달 형태로 제공
 *
 * **V26.2 변경사항 (회사별 사번 포맷):**
 * - KR 소속: 0001 형식 (4자리 숫자)
 * - CN 소속: CN0001 형식 (CN prefix + 4자리 숫자)
 * - orgUnitId 변경 시 getNextEmployeeNumber callable 호출하여 다음 사번 자동 조회
 * - CN 회사인 경우 사번 입력 필드 앞에 "CN" prefix 표시
 *
 * **V48 변경사항:**
 * - 타이틀에서 (V2) 텍스트 제거
 * - 권한 부여 섹션에서 Shield 아이콘 제거
 * - 성별 옵션 "남성/여성" → "남/여" 변경
 * - 권한 부여 목록을 ASSIGNABLE_ROLE_IDS 순서대로 정렬 (일반 → TL → 조직장 → 인사팀 → 임원)
 * - 파견/인턴/계약직 계약 만료일 필수 + 입사일 이후 검증 추가
 *
 * **V39 변경사항:**
 * - 역할 선택 UI에 한글 라벨 적용
 * - ASSIGNABLE_ROLE_IDS로 이중 필터링 (서버 + 프론트)
 *
 * **V36.8 변경사항:**
 * - 생년월일 입력 필드 제거 (등록번호에서 자동 추출)
 * - 전화번호 자동 하이픈 포맷
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Save, Loader } from 'lucide-react';
import { useOrgUnits } from '../../../shared/hooks/useOrgUnits';
import { useEmployees } from '../../../shared/hooks/useEmployees';
import { usePositionLevels } from '../../../shared/hooks/usePositionLevels';
import { useJobTitles } from '../../../shared/hooks/useJobTitles';
import { useAssignableRoles } from '../../../shared/hooks/useAssignableRoles'; // V37
import {
  useEmployeeCreateMutations,
  CreateEmployeeFormData,
  EducationLevelCode,
} from '../../../shared/hooks/useEmployeeCreateMutations';
import { getRoleLabel, ASSIGNABLE_ROLE_IDS } from '../../../shared/constants/roles'; // V39
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import { useAuthorization } from '../../../shared/hooks/useAuthorization'; // V70.24
import { EmployeePhotoPicker } from './EmployeePhotoPicker'; // V70.24
import { httpsCallable } from 'firebase/functions'; // V70.24
import { functions } from '../../../firebase/config'; // V70.24

/**
 * V36.8: 전화번호 자동 하이픈 포맷
 * 01012345678 → 010-1234-5678
 */
function formatPhoneNumber(value: string): string {
  // 숫자만 추출
  const numbers = value.replace(/\D/g, '');

  // 11자리: 010-1234-5678
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }
  // 10자리: 02-1234-5678 또는 031-123-4567
  if (numbers.length === 10) {
    if (numbers.startsWith('02')) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
  // 9자리: 02-123-4567
  if (numbers.length === 9 && numbers.startsWith('02')) {
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5, 9)}`;
  }

  // 그 외: 숫자만 반환 (진행 중인 입력)
  return numbers;
}

/**
 * HrEmployeeFormModal Props
 */
export interface HrEmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * HrEmployeeFormModal 컴포넌트
 */
export function HrEmployeeFormModal({
  isOpen,
  onClose,
  onSuccess,
}: HrEmployeeFormModalProps) {
  // ============================================
  // 상태 및 Hook
  // ============================================

  const { orgUnits } = useOrgUnits();
  // V26.2: 전체 직원 목록 조회를 위해 status 필터 추가 (필터 없으면 조회 안 됨)
  const { employees } = useEmployees({ status: 'all' });
  const { positionLevels, isLoading: isPositionsLoading } = usePositionLevels({ status: 'active' });
  const { jobTitles, isLoading: isJobTitlesLoading } = useJobTitles({ status: 'active' });
  const { createEmployee, isCreating } = useEmployeeCreateMutations();
  const { roles: assignableRoles, isLoading: isRolesLoading } = useAssignableRoles(); // V37

  // V37: 선택된 역할 ID 상태
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  // ============================================
  // V70.24: 프로필 사진 (선택) — 등록 전에는 업로드하지 않는다
  // ============================================

  /**
   * V70.24: 사진 UI 노출 권한.
   *
   * BE `uploadEmployeePhoto` 는 `checkIsHrOrAdmin` 으로 막혀 있고,
   * 그 역할 집합은 `HR_OR_ADMIN_ROLES = ['role_hr', 'system_admin']` 이다.
   * (functions/src/modules/auth/roles.ts)
   *
   * 직원 생성 권한(`checkIsAdminLike`)에는 role_admin_kr / role_admin_cn 도 포함되지만
   * 이 두 역할은 사진 업로드가 거부되므로 **사진 영역 자체를 노출하지 않는다.**
   * (권한 정책은 변경하지 않는다 — FE 노출만 BE 게이트에 맞춘다)
   */
  const { hasAnyRole } = useAuthorization();
  const canUploadPhoto = hasAnyRole(['role_hr', 'system_admin']);

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);

  /** V70.24: 사진 선택/해제 — 로컬 preview 만 갱신 (Storage 접근 없음) */
  const handlePhotoChange = (selected: File | null) => {
    setPhotoFile(selected);
    if (!selected) {
      setPhotoPreviewUrl(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotoPreviewUrl(reader.result as string);
    reader.onerror = () => setPhotoPreviewUrl(null);
    reader.readAsDataURL(selected);
  };

  /**
   * V70.24: 제출 전체(직원 생성 + 사진 업로드) 구간 잠금.
   *
   * `isCreating` 은 createEmployee() resolve 시점에 false 로 돌아가므로,
   * 사진 업로드가 이어지는 동안 폼이 다시 활성화되는 것을 막기 위해 별도 state 를 둔다.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** 폼 잠금 기준 — 기존 isCreating 과 병행 */
  const isBusy = isCreating || isSubmitting;

  // ============================================
  // V29.17: 회사별 사번 자동 부여 (호출자 스코프 기반)
  // ============================================
  // V65: 회사 코드 상태
  const [companyCode, setCompanyCode] = useState<'KR' | 'CN' | null>(null);
  // V65: 사번 prefix (KR/KRD/CN/CND)
  const [employeeNumberPrefix, setEmployeeNumberPrefix] = useState<string>('');
  // V65: 조직 미선택 상태
  const [needsOrgSelection, setNeedsOrgSelection] = useState<boolean>(false);

  /**
   * V65: orgUnit 트리에서 companyCode 판정 (FE 로컬)
   */
  const resolveCompanyCode = useCallback((orgUnitId: string): 'KR' | 'CN' | null => {
    if (!orgUnits || orgUnits.length === 0) return null;
    const orgMap = new Map(orgUnits.map(o => [o.id, o]));
    let currentId: string | null = orgUnitId;
    let depth = 0;
    while (currentId && depth < 10) {
      const org = orgMap.get(currentId);
      if (!org) break;
      if (org.type === 'company' || !org.parentId) {
        return (org.companyCode as 'KR' | 'CN') || null;
      }
      currentId = org.parentId;
      depth++;
    }
    return null;
  }, [orgUnits]);

  /**
   * V65: prefix 미리보기 계산 (companyCode + employmentType → KR/KRD/CN/CND)
   */
  const getPreviewPrefix = useCallback((cc: 'KR' | 'CN' | null, empType: string): string => {
    if (!cc) return '';
    const isDispatch = empType === 'DISPATCH';
    if (cc === 'CN') return isDispatch ? 'CND' : 'CN';
    return isDispatch ? 'KRD' : 'KR';
  }, []);

  // ============================================
  // 폼 상태
  // ============================================
  const [formData, setFormData] = useState<CreateEmployeeFormData>({
    nameKo: '',
    email: '',
    employeeNumber: '',
    gender: undefined,
    // V36.8: birthDate 제거 (등록번호에서 자동 추출)
    affiliationName: '엔글', // V62: 소속 — 엔글소속 기본값
    orgUnitId: '',
    positionId: '',
    jobTitleId: '',
    employmentType: 'REGULAR',
    regularHireDate: '',
    dispatchHireDate: '',
    dispatchEndDate: '',
    internHireDate: '',
    internEndDate: '',
    contractHireDate: '',
    contractEndDate: '',
    nationalityType: 'domestic',
    residentRegistrationNo: '',
    phoneNumber: '',
    address: '',
    educationLevelCode: undefined,
    major: '',
    priorCareerYears: 0, // V59: 이전경력
    promotionBonusYears: 0, // V61: 진급 가산 연한
  });

  // V2: 이메일 로컬 파트 (@ 앞부분)
  const [emailLocalPart, setEmailLocalPart] = useState('');

  const [error, setError] = useState<string | null>(null);

  // ============================================
  // V29.17: 모달 열릴 때 + orgUnitId 변경 시 사번 조회
  // ============================================

  // V65: 사번은 서버에서 자동 채번 — FE에서 getNextEmployeeNumber 호출 불필요
  // companyCode는 orgUnitId 변경 시 위의 resolveCompanyCode로 로컬 판정

  // ============================================
  // 중복 체크
  // ============================================
  // V65: 사번 중복 체크 제거 (서버에서 자동 채번하므로 FE 체크 불필요)

  const isDuplicateEmail = useMemo(() => {
    if (!emailLocalPart || !employees) return false;
    const fullEmail = `${emailLocalPart}@ngle.co.kr`;
    return employees.some((emp) => emp.email === fullEmail);
  }, [emailLocalPart, employees]);

  // V65: orgUnitId 변경 시 companyCode 로컬 판정
  useEffect(() => {
    if (formData.orgUnitId) {
      const resolved = resolveCompanyCode(formData.orgUnitId);
      setCompanyCode(resolved);
      setNeedsOrgSelection(false);
    } else {
      setCompanyCode(null);
    }
  }, [formData.orgUnitId, resolveCompanyCode]);

  // V65: companyCode 또는 employmentType 변경 시 prefix 미리보기 업데이트
  useEffect(() => {
    const prefix = getPreviewPrefix(companyCode, formData.employmentType || 'REGULAR');
    setEmployeeNumberPrefix(prefix);
  }, [companyCode, formData.employmentType, getPreviewPrefix]);

  // V62: 고용형태 변경 시 소속명 자동 입력
  useEffect(() => {
    if (formData.employmentType === 'DISPATCH') {
      // 파견직 전환 시: 기존 '엔글' 자동값 제거, 수동 입력 유도
      if (formData.affiliationName === '엔글') {
        setFormData((prev) => ({ ...prev, affiliationName: '' }));
      }
    } else {
      // 정규직/인턴/계약직: 빈값이면 '엔글' 자동 입력
      if (!formData.affiliationName) {
        setFormData((prev) => ({ ...prev, affiliationName: '엔글' }));
      }
    }
  }, [formData.employmentType]);

  // ============================================
  // 이벤트 핸들러
  // ============================================

  /**
   * 폼 필드 변경 핸들러
   */
  const handleChange = (field: keyof CreateEmployeeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  /**
   * 이메일 로컬 파트 변경 핸들러 (V2)
   */
  const handleEmailLocalPartChange = (value: string) => {
    setEmailLocalPart(value);
    setError(null);
  };

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // V70.24: 제출 중 재진입 차단 (사진 업로드까지 포함한 전체 구간)
    if (isBusy) return;

    // 필수 필드 검증
    if (!formData.nameKo) {
      setError('이름(한글)은 필수입니다.');
      return;
    }

    // V2: 이메일 조합
    if (!emailLocalPart.trim()) {
      setError('이메일은 필수입니다.');
      return;
    }
    if (isDuplicateEmail) {
      setError('이미 사용 중인 이메일입니다.');
      return;
    }

    // V65: 사번 검증 제거 (서버에서 자동 채번)

    if (!formData.orgUnitId) {
      setError('부서는 필수입니다.');
      return;
    }
    if (!formData.positionId) {
      setError('직위는 필수입니다.');
      return;
    }
    if (!formData.jobTitleId) {
      setError('직책은 필수입니다.');
      return;
    }

    // V2: 고용 정보 검증
    if (!formData.employmentType) {
      setError('계약형태는 필수입니다.');
      return;
    }

    // V62: 파견직 파견회사명 필수 검증
    if (formData.employmentType === 'DISPATCH' && !formData.affiliationName?.trim()) {
      setError('파견직은 파견회사명(소속) 입력이 필수입니다.');
      return;
    }

    switch (formData.employmentType) {
      case 'REGULAR':
        if (!formData.regularHireDate) {
          setError('정규 입사일은 필수입니다.');
          return;
        }
        break;
      case 'DISPATCH':
        if (!formData.dispatchHireDate) {
          setError('파견 입사일은 필수입니다.');
          return;
        }
        // V48: 계약 만료일 필수 및 날짜 검증
        if (!formData.dispatchEndDate) {
          setError('파견 계약 만료일은 필수입니다.');
          return;
        }
        if (formData.dispatchEndDate < formData.dispatchHireDate) {
          setError('파견 계약 만료일은 입사일 이후여야 합니다.');
          return;
        }
        break;
      case 'INTERN':
        if (!formData.internHireDate) {
          setError('인턴 입사일은 필수입니다.');
          return;
        }
        // V48: 계약 만료일 필수 및 날짜 검증
        if (!formData.internEndDate) {
          setError('인턴 계약 만료일은 필수입니다.');
          return;
        }
        if (formData.internEndDate < formData.internHireDate) {
          setError('인턴 계약 만료일은 입사일 이후여야 합니다.');
          return;
        }
        break;
      case 'CONTRACT':
        if (!formData.contractHireDate) {
          setError('계약직 입사일은 필수입니다.');
          return;
        }
        // V48: 계약 만료일 필수 및 날짜 검증
        if (!formData.contractEndDate) {
          setError('계약직 계약 만료일은 필수입니다.');
          return;
        }
        if (formData.contractEndDate < formData.contractHireDate) {
          setError('계약직 계약 만료일은 입사일 이후여야 합니다.');
          return;
        }
        break;
    }

    try {
      // 이메일 조합
      const fullEmail = `${emailLocalPart.trim()}@ngle.co.kr`;

      // V2: 민감정보 정리
      let submitData: CreateEmployeeFormData = {
        ...formData,
        email: fullEmail,
      };

      // 주민등록번호 전송
      if (formData.residentRegistrationNo) {
        submitData = {
          ...submitData,
          residentRegistrationNo: formData.residentRegistrationNo,
        };
      }

      // V37: 선택된 역할 추가
      if (selectedRoleIds.length > 0) {
        submitData = {
          ...submitData,
          selectedRoleIds,
        };
      }

      setIsSubmitting(true);
      const result = await createEmployee(submitData);
      const generatedNumber = result?.employeeNumber || '';
      const baseMessage = `직원이 성공적으로 생성되었습니다.${generatedNumber ? ` (사번: ${generatedNumber})` : ''}`;

      // ── V70.24: 직원 생성 성공 후에만 사진 업로드 (기존 callable 재사용)
      //   - 생성이 실패하면 이 블록에 도달하지 않으므로 Storage orphan 이 발생하지 않는다.
      //   - 사진 업로드 실패는 부가정보 실패로 간주하고 **직원 생성을 rollback 하지 않는다.**
      // V70.24-2: 훅 반환 계약(useEmployeeCreateMutations)에서 그대로 사용한다.
      //   `as any` 로 캐스팅하지 않는다 — 계약이 바뀌면 TypeScript 가 잡아야 한다.
      const newUserId = result?.userId;
      let photoErrorMessage: string | null = null;

      // V70.24-2: 사진을 선택했는데 userId 를 못 받은 경우를 조용히 넘기지 않는다.
      //   (이전에는 조건 불충족 시 분기 자체를 건너뛰어 "생성 성공" 만 안내되었다)
      if (canUploadPhoto && photoFile && !newUserId) {
        photoErrorMessage = '생성된 직원 정보를 확인하지 못했습니다.';
      }

      if (canUploadPhoto && photoFile && newUserId) {
        try {
          if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

          const fileBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result as string;
              resolve(dataUrl.split(',')[1]); // data URI prefix 제거
            };
            reader.onerror = () => reject(new Error('파일을 읽을 수 없습니다.'));
            reader.readAsDataURL(photoFile);
          });

          const uploadEmployeePhoto = httpsCallable<
            { employeeId: string; fileBase64: string; contentType: string; fileName: string },
            { success: boolean; photoPath: string }
          >(functions, 'uploadEmployeePhoto');

          const uploadResult = await uploadEmployeePhoto({
            employeeId: newUserId,
            fileBase64,
            contentType: photoFile.type,
            fileName: photoFile.name,
          });

          if (!uploadResult.data?.success) throw new Error('업로드 실패');
        } catch (photoErr: any) {
          photoErrorMessage = photoErr?.message || '사진 업로드에 실패했습니다.';
          console.error(
            '[HrEmployeeFormModal] 프로필 사진 업로드 실패:',
            photoErr instanceof Error ? photoErr.message : String(photoErr)
          );
        }
      }

      if (photoErrorMessage) {
        alert(
          `${baseMessage}\n\n` +
            '다만 프로필 사진 등록에 실패했습니다.\n' +
            '직원 상세 화면에서 사진을 다시 등록해 주세요.\n\n' +
            `(사유: ${photoErrorMessage})`
        );
      } else {
        alert(baseMessage);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || '직원 생성에 실패했습니다.');
    } finally {
      // V70.24: 사진 업로드까지 끝난 뒤에만 잠금 해제
      setIsSubmitting(false);
    }
  };

  /**
   * 모달 닫기 핸들러
   */
  const handleClose = () => {
    if (isBusy) return;   // V70.24: 사진 업로드 구간까지 닫기 차단
    setFormData({
      nameKo: '',
      email: '',
      employeeNumber: '',
      gender: undefined,
      // V36.8: birthDate 제거
      affiliationName: '엔글', // V62: 소속 — 엔글소속 기본값
      orgUnitId: '',
      positionId: '',
      jobTitleId: '',
      employmentType: 'REGULAR',
      regularHireDate: '',
      dispatchHireDate: '',
      dispatchEndDate: '',
      internHireDate: '',
      internEndDate: '',
      contractHireDate: '',
      contractEndDate: '',
      nationalityType: 'domestic',
      residentRegistrationNo: '',
      phoneNumber: '',
      address: '',
      educationLevelCode: undefined,
      major: '',
      priorCareerYears: 0, // V59: 이전경력 초기화
      promotionBonusYears: 0, // V61: 진급 가산 초기화
    });
    setEmailLocalPart('');
    setSelectedRoleIds([]); // V37: 역할 선택 초기화
    setPhotoFile(null); // V70.24: 선택된 사진 초기화 (Storage 작업 없음)
    setPhotoPreviewUrl(null);
    // V65: 사번 상태 초기화
    setCompanyCode(null);
    setEmployeeNumberPrefix('');
    setNeedsOrgSelection(false);
    setError(null);
    onClose();
  };

  const overlayProps = useModalOverlayClose(handleClose);

  // ============================================
  // 렌더링
  // ============================================

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} {...overlayProps}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>직원 추가</h2>
          <button
            onClick={handleClose}
            disabled={isBusy}
            style={{
              ...closeButtonStyle,
              cursor: isBusy ? 'not-allowed' : 'pointer',
              opacity: isBusy ? 0.5 : 1,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 에러 메시지 */}
        {error && <div style={errorBoxStyle}>{error}</div>}

        {/* 폼 */}
        <form onSubmit={handleSubmit} style={formStyle}>
          {/* ========== 기본 정보 ========== */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>기본 정보</h3>

            {/* V70.24: 프로필 사진 (선택) — role_hr / system_admin 에게만 노출.
                등록 전에는 업로드하지 않고 로컬 preview 만 유지한다. */}
            {canUploadPhoto && (
              <div style={{ ...fieldGroupStyle, display: 'flex', justifyContent: 'center' }}>
                <EmployeePhotoPicker
                  file={photoFile}
                  previewUrl={photoPreviewUrl}
                  onChange={handlePhotoChange}
                  disabled={isBusy}
                />
              </div>
            )}

            {/* 이름 (한글) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                이름 (한글) <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.nameKo}
                onChange={(e) => handleChange('nameKo', e.target.value)}
                style={inputStyle}
                disabled={isBusy}
                placeholder="홍길동"
              />
            </div>

            {/* V36.8: 이름 (영문) */}
            {/* 이메일 (V2: @ 앞부분만 입력) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                이메일 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={emailLocalPart}
                  onChange={(e) => handleEmailLocalPartChange(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                  disabled={isBusy}
                  placeholder="hong.gd"
                />
                <span style={{ color: 'var(--color-gray-600)', fontSize: '14px', minWidth: '100px' }}>
                  @ngle.co.kr
                </span>
              </div>
              {isDuplicateEmail && (
                <div style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>
                  ⚠️ 이미 사용 중인 이메일입니다.
                </div>
              )}
            </div>

            {/* V65: 사번 (자동 채번 — 서버에서 생성 시 결정) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>사번</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* V65: prefix 미리보기 배지 */}
                {employeeNumberPrefix && (
                  <span style={{
                    backgroundColor: 'var(--color-primary-100)',
                    color: 'var(--color-primary-700)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontWeight: 600,
                    fontSize: '14px',
                    border: '1px solid var(--color-primary-300)',
                    minWidth: '40px',
                    textAlign: 'center',
                  }}>
                    {employeeNumberPrefix}
                  </span>
                )}
                <span style={{
                  ...inputStyle,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--color-gray-500)',
                  backgroundColor: 'var(--color-gray-50)',
                  cursor: 'default',
                }}>
                  {formData.orgUnitId ? '저장 시 자동 생성' : '조직 선택 후 자동 생성'}
                </span>
              </div>
              {formData.orgUnitId && employeeNumberPrefix && (
                <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                  * 저장 시 {employeeNumberPrefix}XXXX 형식으로 자동 채번됩니다.
                </div>
              )}
            </div>

            {/* 성별 (V2: other 제거) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>성별</label>
              <select
                value={formData.gender || ''}
                onChange={(e) =>
                  handleChange('gender', e.target.value === '' ? undefined : e.target.value)
                }
                style={inputStyle}
                disabled={isBusy}
              >
                <option value="">선택하세요</option>
                <option value="male">남성</option>
                <option value="female">여성</option>
              </select>
              <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                * 미선택 시 등록번호에서 자동 추출됩니다.
              </div>
            </div>

            {/* V36.8: 생년월일 필드 제거 - 등록번호에서 자동 추출 */}
          </div>

          {/* ========== 고용 정보 (V62: 소속 정보보다 위로 이동) ========== */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>고용 정보</h3>

            {/* 계약형태 (V2) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                계약형태 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.employmentType}
                onChange={(e) => handleChange('employmentType', e.target.value)}
                style={inputStyle}
                disabled={isBusy}
              >
                <option value="REGULAR">정규직</option>
                <option value="DISPATCH">파견직</option>
                <option value="INTERN">인턴</option>
                <option value="CONTRACT">계약직</option>
              </select>
            </div>

            {/* 정규직 입사일 + 이전경력 */}
            {formData.employmentType === 'REGULAR' && (
              <>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    정규 입사일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.regularHireDate}
                    onChange={(e) => handleChange('regularHireDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                  />
                </div>

                {/* 이전경력 - 정규직일 때 정규 입사일 바로 아래 */}
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>이전경력 (년)</label>
                  <input
                    type="number"
                    value={formData.priorCareerYears || 0}
                    onChange={(e) => handleChange('priorCareerYears', parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                    disabled={isBusy}
                    placeholder="0"
                    step="0.5"
                    min="0"
                  />
                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                    진급 계산에 반영되는 이전 경력 연한 (소수점 허용)
                  </div>
                </div>
              </>
            )}

            {/* 파견직 */}
            {formData.employmentType === 'DISPATCH' && (
              <>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    파견 입사일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.dispatchHireDate}
                    onChange={(e) => handleChange('dispatchHireDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                  />
                </div>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    파견 계약 만료일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.dispatchEndDate}
                    onChange={(e) => handleChange('dispatchEndDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                    min={formData.dispatchHireDate || undefined}
                  />
                  {formData.dispatchHireDate && (
                    <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                      * 입사일({formData.dispatchHireDate}) 이후 날짜만 선택 가능합니다.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 인턴 (V2) */}
            {formData.employmentType === 'INTERN' && (
              <>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    인턴 입사일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.internHireDate}
                    onChange={(e) => handleChange('internHireDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                  />
                </div>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    인턴 계약 만료일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.internEndDate}
                    onChange={(e) => handleChange('internEndDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                    min={formData.internHireDate || undefined}
                  />
                  {formData.internHireDate && (
                    <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                      * 입사일({formData.internHireDate}) 이후 날짜만 선택 가능합니다.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 계약직 (V2) */}
            {formData.employmentType === 'CONTRACT' && (
              <>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    계약직 입사일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.contractHireDate}
                    onChange={(e) => handleChange('contractHireDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                  />
                </div>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>
                    계약직 계약 만료일 <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.contractEndDate}
                    onChange={(e) => handleChange('contractEndDate', e.target.value)}
                    style={inputStyle}
                    disabled={isBusy}
                    min={formData.contractHireDate || undefined}
                  />
                  {formData.contractHireDate && (
                    <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                      * 입사일({formData.contractHireDate}) 이후 날짜만 선택 가능합니다.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ========== 소속 정보 (V62: 고용 정보 아래로 이동) ========== */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>소속 정보</h3>

            {/* V62: 소속 (회사/파견업체명) — 고용형태에 따라 자동 입력/수동 입력 */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                {formData.employmentType === 'DISPATCH' ? '파견회사명' : '소속'}
                {formData.employmentType === 'DISPATCH' && (
                  <span style={{ color: 'var(--color-error)' }}> *</span>
                )}
              </label>
              <input
                type="text"
                value={formData.affiliationName || ''}
                onChange={(e) => handleChange('affiliationName', e.target.value)}
                placeholder={
                  formData.employmentType === 'DISPATCH'
                    ? '파견업체명을 입력하세요'
                    : '엔글'
                }
                style={{
                  ...inputStyle,
                  ...(formData.employmentType !== 'DISPATCH' ? { backgroundColor: 'var(--color-gray-50, #f9fafb)' } : {}),
                }}
                disabled={isBusy}
              />
              {formData.employmentType === 'DISPATCH' && (
                <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                  * 파견직은 파견회사명 입력이 필수입니다.
                </div>
              )}
              {formData.employmentType !== 'DISPATCH' && (
                <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                  * 엔글 소속 직원은 자동으로 '엔글'이 입력됩니다.
                </div>
              )}
            </div>

            {/* 부서 (기존 소속 조직) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                부서 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.orgUnitId}
                onChange={(e) => handleChange('orgUnitId', e.target.value)}
                style={inputStyle}
                disabled={isBusy}
              >
                <option value="">선택하세요</option>
                {orgUnits
                  .filter((org) => org.status === 'active')
                  .map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* 직위 (V2: 마스터 데이터 선택) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                직위 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.positionId}
                onChange={(e) => handleChange('positionId', e.target.value)}
                style={inputStyle}
                disabled={isBusy || isPositionsLoading || positionLevels.length === 0}
              >
                {isPositionsLoading ? (
                  <option value="">직위 정보를 불러오는 중입니다...</option>
                ) : positionLevels.length === 0 ? (
                  <option value="" disabled>
                    등록된 직위가 없습니다.
                  </option>
                ) : (
                  <>
                    <option value="">선택하세요</option>
                    {positionLevels.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {!isPositionsLoading && positionLevels.length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--color-warning, #f59e0b)', marginTop: '4px' }}>
                  * 마스터데이터 &gt; 직위관리에서 직위를 먼저 등록해주세요.
                </div>
              )}
            </div>

            {/* 직책 (V2: 마스터 데이터 선택) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                직책 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.jobTitleId}
                onChange={(e) => handleChange('jobTitleId', e.target.value)}
                style={inputStyle}
                disabled={isBusy || isJobTitlesLoading || jobTitles.length === 0}
              >
                {isJobTitlesLoading ? (
                  <option value="">직책 정보를 불러오는 중입니다...</option>
                ) : jobTitles.length === 0 ? (
                  <option value="" disabled>
                    등록된 직책이 없습니다.
                  </option>
                ) : (
                  <>
                    <option value="">선택하세요</option>
                    {jobTitles.map((title) => (
                      <option key={title.id} value={title.id}>
                        {title.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {!isJobTitlesLoading && jobTitles.length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--color-warning, #f59e0b)', marginTop: '4px' }}>
                  * 마스터데이터 &gt; 직책관리에서 직책을 먼저 등록해주세요.
                </div>
              )}
            </div>
          </div>

          {/* ========== 민감 정보 (V2: 내/외국인 구분) ========== */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>민감 정보 (선택)</h3>

            {/* 내/외국인 구분 (V2) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>국적</label>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="nationalityType"
                    value="domestic"
                    checked={formData.nationalityType === 'domestic'}
                    onChange={(e) => handleChange('nationalityType', 'domestic')}
                    disabled={isBusy}
                  />
                  <span>내국인</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="nationalityType"
                    value="foreign"
                    checked={formData.nationalityType === 'foreign'}
                    onChange={(e) => handleChange('nationalityType', 'foreign')}
                    disabled={isBusy}
                  />
                  <span>외국인</span>
                </label>
              </div>
            </div>

            {/* 주민등록번호 (내국인 KR) / 공민신분번호 (내국인 CN) */}
            {formData.nationalityType === 'domestic' && (
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>
                  {companyCode === 'CN' ? '공민신분번호 (居民身份证号)' : '주민등록번호'}
                </label>
                <input
                  type="text"
                  value={formData.residentRegistrationNo}
                  onChange={(e) => handleChange('residentRegistrationNo', e.target.value)}
                  style={inputStyle}
                  disabled={isBusy}
                  placeholder={companyCode === 'CN' ? '18자리 공민신분번호 입력' : '123456-1234567'}
                  maxLength={companyCode === 'CN' ? 18 : 14}
                />
                <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                  {companyCode === 'CN'
                    ? '* 선택 사항입니다. 18자리 공민신분번호를 입력해주세요.'
                    : '* 선택 사항입니다. 입력 시 안전하게 저장됩니다.'}
                </div>
              </div>
            )}

          </div>

          {/* ========== 연락처/주소/학력/전공 (V2 추가) ========== */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>추가 정보 (선택)</h3>

            {/* 전화번호 (V36.8: 자동 하이픈 포맷) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>전화번호</label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', formatPhoneNumber(e.target.value))}
                style={inputStyle}
                disabled={isBusy}
                placeholder="010-1234-5678"
                maxLength={13}
              />
              <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                * 숫자 입력 시 자동으로 하이픈이 추가됩니다.
              </div>
            </div>

            {/* 주소 (V2) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>주소</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                style={inputStyle}
                disabled={isBusy}
                placeholder="서울시 강남구..."
              />
            </div>

            {/* 최종학력 (V2) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>최종학력</label>
              <select
                value={formData.educationLevelCode || ''}
                onChange={(e) =>
                  handleChange(
                    'educationLevelCode',
                    e.target.value === '' ? undefined : (e.target.value as EducationLevelCode)
                  )
                }
                style={inputStyle}
                disabled={isBusy}
              >
                <option value="">선택하세요</option>
                <option value="HIGH_SCHOOL">고졸</option>
                <option value="ASSOCIATE_2">초대졸 (2년)</option>
                <option value="ASSOCIATE_3">초대졸 (3년)</option>
                <option value="BACHELOR">대졸</option>
                <option value="MASTER">대학원졸</option>
              </select>
            </div>

            {/* 전공 (V2) */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>전공</label>
              <input
                type="text"
                value={formData.major}
                onChange={(e) => handleChange('major', e.target.value)}
                style={inputStyle}
                disabled={isBusy}
                placeholder="컴퓨터공학"
              />
            </div>
            {/* V64: 이전경력은 고용 정보 섹션의 정규 입사일 아래로 이동됨 */}
            {/* V64: 가산(promotionBonusYears)은 직원 추가 모달에서 제거됨 - 고용형태 변경 시 전환가산으로 처리 */}
          </div>

          {/* ========== V39: 역할(권한) 부여 (한글 라벨 적용) ========== */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>권한 부여 (선택)</h3>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              신규 직원에게 부여할 역할을 선택하세요. 선택하지 않으면 기본 권한 없이 생성됩니다.
            </div>

            {isRolesLoading ? (
              <div style={{ color: '#6b7280', fontSize: '14px' }}>역할 목록 로딩 중...</div>
            ) : (() => {
                // V48: ASSIGNABLE_ROLE_IDS 순서대로 정렬 (일반 → TL → 조직장 → 인사팀 → 임원)
                const filteredRoles = assignableRoles
                  .filter(role => ASSIGNABLE_ROLE_IDS.includes(role.id))
                  .sort((a, b) => ASSIGNABLE_ROLE_IDS.indexOf(a.id) - ASSIGNABLE_ROLE_IDS.indexOf(b.id));

                if (filteredRoles.length === 0) {
                  return <div style={{ color: '#6b7280', fontSize: '14px' }}>부여 가능한 역할이 없습니다.</div>;
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredRoles.map((role) => (
                      <label
                        key={role.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 12px',
                          backgroundColor: selectedRoleIds.includes(role.id) ? '#eff6ff' : '#f9fafb',
                          borderRadius: '8px',
                          border: selectedRoleIds.includes(role.id) ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                          cursor: isBusy ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRoleIds([...selectedRoleIds, role.id]);
                            } else {
                              setSelectedRoleIds(selectedRoleIds.filter((id) => id !== role.id));
                            }
                          }}
                          disabled={isBusy}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <div>
                          {/* V39: 한글 라벨 적용 */}
                          <div style={{ fontWeight: 500, fontSize: '14px' }}>{getRoleLabel(role.id)}</div>
                          {role.description && (
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{role.description}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                );
              })()
            }
          </div>

          {/* ========== 버튼 그룹 ========== */}
          <div style={buttonGroupStyle}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isBusy}
              style={{
                ...cancelButtonStyle,
                cursor: isBusy ? 'not-allowed' : 'pointer',
                opacity: isBusy ? 0.5 : 1,
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isBusy || isDuplicateEmail}
              style={{
                ...submitButtonStyle,
                cursor:
                  isBusy || isDuplicateEmail
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  isBusy || isDuplicateEmail ? 0.7 : 1,
              }}
            >
              {isBusy ? (
                <>
                  <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>생성 중...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>저장</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// 스타일 정의
// ============================================

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '650px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};

const headerStyle: React.CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--color-gray-200)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  background: 'white',
  zIndex: 1,
};

const titleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
  margin: 0,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '8px',
  cursor: 'pointer',
  color: 'var(--color-gray-600)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  transition: 'background 0.2s',
};

const formStyle: React.CSSProperties = {
  padding: '24px',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '32px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: '600',
  color: 'var(--color-gray-700)',
  marginBottom: '16px',
};

const fieldGroupStyle: React.CSSProperties = {
  marginBottom: '16px',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: 'var(--color-gray-700)',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const errorBoxStyle: React.CSSProperties = {
  margin: '0 24px 16px 24px',
  padding: '12px',
  background: 'var(--color-error-light, #fee)',
  border: '1px solid var(--color-error)',
  borderRadius: '6px',
  color: 'var(--color-error)',
  fontSize: '14px',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  marginTop: '24px',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  background: 'white',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  color: 'var(--color-gray-700)',
  cursor: 'pointer',
  transition: 'background 0.2s',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--color-primary)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'opacity 0.2s',
};
